import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Car, Upload, Sparkles, Loader2, ChevronRight, ChevronDown, CheckCircle, Clock, Grid3x3, Grid2x2, Square, Star, Trash2, X, History, Plus, Home, Image as ImageIcon, Palette } from 'lucide-react';
import AppHeader from '../shared/AppHeader';
import V6ModernPicker from '../shared/V6ModernPicker';
import VehicleGallery from './VehicleGallery';
import VehicleModifyModal from './VehicleModifyModal';
import { VEHICLE_PROMPT_TEMPLATES } from '../../data/vehiclePromptTemplates';
import { PROMPT_TEMPLATES as AVATAR_PROMPT_TEMPLATES } from '../../data/promptTemplates';
import { apiClient } from '../../utils/apiClient';
import { getKeywordsForValue, getLabelForValue, IMAGE_FORMAT_LABELS } from '../../utils/dropdownKeywords';

export default function VehicleDashboard({ currentUser, onLogout }) {
  // Multi-Training Support (wie bei Avataren)
  const [trainings, setTrainings] = useState([]);
  const [activeTrainingIndex, setActiveTrainingIndex] = useState(0);
  const [expandedVehicleId, setExpandedVehicleId] = useState(null);  // NEU: Für Trainingsfotos-Submenu
  const [showTrainingSwitcher, setShowTrainingSwitcher] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [trainingType, setTrainingType] = useState('vehicle');  // 'vehicle' or 'avatar'
  const [gridView, setGridView] = useState(2);  // Grid-Ansicht (1, 2 oder 4)
  const [isLoadingTrainings, setIsLoadingTrainings] = useState(true);  // NEU: Loading State
  
  // Computed: Current Training
  const currentTraining = trainings.length > 0 ? trainings[activeTrainingIndex] : null;
  const hasTraining = trainings.length > 0;
  
  // useRef für Polling ohne Re-Renders (wie V1)
  const trainingsRef = useRef(trainings);
  const activeTrainingIndexRef = useRef(activeTrainingIndex);
  
  // Refs aktualisieren wenn State sich ändert
  useEffect(() => {
    trainingsRef.current = trainings;
  }, [trainings]);
  
  useEffect(() => {
    activeTrainingIndexRef.current = activeTrainingIndex;
  }, [activeTrainingIndex]);
  
  // Wizard State
  const [vehiclePhotos, setVehiclePhotos] = useState([]);
  const [vehiclePhotoPreviews, setVehiclePhotoPreviews] = useState([]);
  const [loadingPreviews, setLoadingPreviews] = useState(false);  // NEU: Loading state
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [color, setColor] = useState('');
  const [year, setYear] = useState('');
  const [vehicleType, setVehicleType] = useState('');
  const [uploading, setUploading] = useState(false);
  
  // Avatar-Felder (NEU!)
  const [avatarName, setAvatarName] = useState('');
  const [gender, setGender] = useState('');
  const [age, setAge] = useState('');
  const [ethnicity, setEthnicity] = useState('');
  const [eyeColor, setEyeColor] = useState('');
  const [bodyType, setBodyType] = useState('');
  const [height, setHeight] = useState('');
  const [hairDescription, setHairDescription] = useState('');
  const [breastSize, setBreastSize] = useState('');

  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Generation State
  const [prompt, setPrompt] = useState('');
  const [numImages, setNumImages] = useState(2);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generations, setGenerations] = useState([]);
  const [loadingGallery, setLoadingGallery] = useState(true);
  
  // V6 Dropdowns
  const [timeOfDay, setTimeOfDay] = useState('day');
  const [weather, setWeather] = useState('clear');
  const [camera, setCamera] = useState('35mm');
  const [style, setStyle] = useState('realistic');
  const [substyle, setSubstyle] = useState('natural');
  const [imageFormat, setImageFormat] = useState('landscape_16_9');
  
  const [menuOpen, setMenuOpen] = useState(false);
  
  // Modify Modal State
  const [showModifyModal, setShowModifyModal] = useState(false);
  const [modifySourceImage, setModifySourceImage] = useState(null);
  const [modifyTrainingId, setModifyTrainingId] = useState(null);
  const [isModifying, setIsModifying] = useState(false);
  
  // Accordion States
  const [accordionOpen, setAccordionOpen] = useState({
    createImage: false,
    history: false,
    myVehicles: false,  // Jetzt geschlossen - User muss klicken
    trainingPhotos: false,
    avatarDetails: false  // NEU: Avatar-Details Panel
  });
  
  // Template & History States
  const [showTemplates, setShowTemplates] = useState(false);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  
  useEffect(() => {
    const token = localStorage.getItem('snapshot_token');
    if (!token) {
      window.location.href = '/welcome';
      return;
    }
    
    // Sofort gecachte Trainings laden (für instant UI)
    const cachedTrainings = localStorage.getItem('snapshot_cached_trainings');
    if (cachedTrainings) {
      try {
        const parsed = JSON.parse(cachedTrainings);
        setTrainings(parsed);
        setIsLoadingTrainings(false);  // Loading sofort beenden
        console.log('⚡ Cached trainings loaded instantly:', parsed.length);
      } catch (e) {
        console.error('Error parsing cached trainings:', e);
      }
    }
    
    // Dann im Hintergrund aktualisieren
    loadTrainings();
    loadGenerations();
  }, []);
  
  // Persist activeTrainingIndex to localStorage
  useEffect(() => {
    if (trainings.length > 0 && activeTrainingIndex >= 0 && activeTrainingIndex < trainings.length) {
      localStorage.setItem('snapshot_last_vehicle_index', activeTrainingIndex.toString());
      console.log(`💾 Persisted active vehicle: ${trainings[activeTrainingIndex].brand} ${trainings[activeTrainingIndex].model} (index: ${activeTrainingIndex})`);
    }
  }, [activeTrainingIndex, trainings]);

  // Helper: Display-Name für Training (Vehicle oder Avatar)
  const getTrainingDisplayName = (training) => {
    if (training?.training_type === 'avatar') {
      return `${training.name || 'Avatar'}`;
    }
    return `${training.brand || ''} ${training.model || ''}`.trim();
  };
  
  const getTrainingSubtitle = (training) => {
    if (training?.training_type === 'avatar') {
      return `${training.gender || ''} ${training.age ? `· ${training.age}` : ''}`.trim();
    }
    return training.color || '';
  };
  
  
  // Auto-refresh während Training läuft
  useEffect(() => {
    // Kontinuierliches Polling wenn Training existiert
    if (!currentTraining) {
      return;
    }
    
    // Poll wenn Training läuft ODER pending ist (für robustes Status-Update)
    if (currentTraining.status === 'training' || currentTraining.status === 'pending') {
      // Poll alle 3 Sekunden für schnellere Updates
      const interval = setInterval(() => {
        console.log('🔄 Auto-refresh: Checking training status & progress...');
        loadTrainings();
      }, 3000);  // 3 Sekunden für flüssigere Progress-Updates
      
      return () => clearInterval(interval);
    }
  }, [currentTraining?.status, currentTraining?.id]);  // Nur bei Status- oder ID-Änderung neu erstellen
  
  const loadTrainings = async () => {
    try {
      const res = await apiClient.get('/api/vehicles/gallery');
      if (!res.ok) return;
      const data = await res.json();
      
      if (data.trainings && data.trainings.length > 0) {
        setTrainings(data.trainings);
        
        // Cache in localStorage für schnelleres Laden
        localStorage.setItem('snapshot_cached_trainings', JSON.stringify(data.trainings));
        
        // Immer das neueste Training auswählen (Index 0 = neuestes)
        setActiveTrainingIndex(0);
      } else {
        setTrainings([]);
        setActiveTrainingIndex(0);
        localStorage.removeItem('snapshot_cached_trainings');
      }
    } catch (error) {
      console.error('Error loading trainings:', error);
    } finally {
      setIsLoadingTrainings(false);  // Loading beendet
    }
  };
  
  const loadGenerations = async () => {
    try {
      setLoadingGallery(true);
      const res = await apiClient.get('/api/vehicles/gallery');
      if (!res.ok) return;
      const data = await res.json();
      setGenerations(data.generations || []);
    } catch (error) {
      console.error('Error loading generations:', error);
    } finally {
      setLoadingGallery(false);
    }
  };
  
  const handleImageDelete = (imageId) => {
    // Just refresh gallery when image is deleted by VehicleGallery
    console.log('🔄 Image deleted, refreshing gallery...', imageId);
    loadGenerations();
  };
  
  // 30-Tage Countdown für Training (wie bei Avataren)
  const getVehicleCountdownInfo = (training) => {
    if (!training || !training.created_at) {
      return { color: 'bg-gray-500', text: 'Unbekannt', glow: 'shadow-gray-500/50', active: false };
    }
    
    const createdDate = new Date(training.created_at);
    const now = new Date();
    const expiryDate = new Date(createdDate);
    expiryDate.setDate(expiryDate.getDate() + 30);
    
    const daysLeft = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
    
    if (daysLeft > 7) {
      return {
        color: 'bg-cyan-400',
        text: `Noch ${daysLeft} Tage`,
        glow: 'shadow-cyan-400/50',
        active: true
      };
    } else if (daysLeft > 0 && daysLeft <= 7) {
      return {
        color: 'bg-yellow-500',
        text: `Noch ${daysLeft} Tage`,
        glow: 'shadow-yellow-500/50',
        active: true
      };
    } else {
      return {
        color: 'bg-red-500',
        text: 'Abgelaufen',
        glow: 'shadow-red-500/50',
        active: false
      };
    }
  };
  
  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    console.log('📸 handlePhotoUpload called, files:', files.length);
    
    if (files.length > 10) {
      alert('Max 10 Bilder!');
      return;
    }
    
    if (files.length === 0) return;
    
    // Set files immediately
    setVehiclePhotos(files);
    setLoadingPreviews(true);  // Start loading
    
    // Create actual previews (NO placeholders!)
    const previews = [];
    let loadedCount = 0;
    
    files.forEach((file, index) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        previews[index] = event.target.result;
        loadedCount++;
        console.log(`📷 Loaded preview ${loadedCount}/${files.length}`);
        
        if (loadedCount === files.length) {
          // Set all previews at once when ALL are loaded
          setVehiclePhotoPreviews(previews.filter(p => p));
          setLoadingPreviews(false);  // Stop loading
          console.log(`✅ All ${loadedCount} image previews loaded`);
        }
      };
      reader.onerror = (error) => {
        console.error(`❌ Error loading preview ${index}:`, error);
        setLoadingPreviews(false);
      };
      reader.readAsDataURL(file);
    });
  };
  
  const handleStartTraining = async () => {
    if (vehiclePhotos.length !== 10) {
      alert('Exakt 10 Fotos erforderlich!');
      return;
    }
    
    // Validation basierend auf trainingType
    if (trainingType === 'vehicle') {
      if (!brand || !model || !color) {
        alert('Bitte Marke, Modell und Farbe angeben!');
        return;
      }
    } else if (trainingType === 'avatar') {
      // ALLE Felder sind Pflichtfelder!
      if (!avatarName || !gender) {
        alert('Bitte Name und Geschlecht angeben!');
        return;
      }
      if (!ethnicity || !eyeColor || !hairDescription) {
        alert('Bitte alle Felder ausfüllen: Ethnie, Augenfarbe, Frisur');
        return;
      }
      if (!bodyType || !height) {
        alert('Bitte Körpertyp und Körpergröße auswählen!');
        return;
      }
      // Brustgröße nur für Frauen Pflicht
      if (gender === 'frau' && !breastSize) {
        alert('Bitte Brustgröße auswählen!');
        return;
      }
    }
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      
      // Add each photo individually
      vehiclePhotos.forEach((photo) => {
        formData.append('images', photo);
      });
      
      // Add training_type
      formData.append('training_type', trainingType);
      
      // Add fields based on type
      if (trainingType === 'vehicle') {
        formData.append('brand', brand);
        formData.append('model', model);
        formData.append('color', color);
        if (year) formData.append('year', year);
        if (vehicleType) formData.append('vehicle_type', vehicleType);
      } else if (trainingType === 'avatar') {
        formData.append('name', avatarName);
        formData.append('gender', gender);
        // Age removed - AI should rely on training photos instead
        if (ethnicity) formData.append('ethnicity', ethnicity);
        if (eyeColor) formData.append('eye_color', eyeColor);
        if (bodyType) formData.append('body_type', bodyType);
        if (height) formData.append('height', height);
        if (hairDescription) formData.append('hair_description', hairDescription);
        if (breastSize && gender === 'frau') formData.append('breast_size', breastSize);
      }
      
      console.log('📤 Uploading:', {
        trainingType,
        photosCount: vehiclePhotos.length,
        ...(trainingType === 'vehicle' ? { brand, model, color, year, vehicleType } : { avatarName, gender, ethnicity })
      });
      
      // Use XMLHttpRequest for progress tracking
      const token = localStorage.getItem('snapshot_token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        
        // Progress tracking
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percentComplete = Math.round((e.loaded / e.total) * 100);
            setUploadProgress(percentComplete);
            console.log(`📊 Upload progress: ${percentComplete}%`);
          }
        });
        
        // Load event (complete)
        xhr.addEventListener('load', () => {
          console.log('📥 Response status:', xhr.status);
          
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const data = JSON.parse(xhr.responseText);
              console.log('✅ Training gestartet:', data);
              resolve(data);
            } catch (e) {
              reject(new Error('Ungültige Server-Antwort'));
            }
          } else {
            const errorText = xhr.responseText;
            console.error('❌ Backend error:', errorText);
            
            let errorMessage = 'Training fehlgeschlagen';
            try {
              const errorJson = JSON.parse(errorText);
              if (errorJson.detail) {
                if (typeof errorJson.detail === 'string') {
                  errorMessage = errorJson.detail;
                } else if (Array.isArray(errorJson.detail)) {
                  errorMessage = errorJson.detail.map(e => `${e.loc.join('.')}: ${e.msg}`).join(', ');
                }
              }
            } catch (e) {
              errorMessage = errorText.substring(0, 200);
            }
            
            reject(new Error(errorMessage));
          }
        });
        
        // Error event
        xhr.addEventListener('error', () => {
          reject(new Error('Netzwerkfehler beim Upload'));
        });
        
        // Abort event
        xhr.addEventListener('abort', () => {
          reject(new Error('Upload abgebrochen'));
        });
        
        // Open and send
        xhr.open('POST', `${backendUrl}/api/vehicles/training/create`);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.send(formData);
      });
      
      // Silent - kein Banner mehr
      setShowWizard(false);
      
      // 🎯 AUTO-SCROLL nach Upload: Nach oben zum Trainings-Status
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 300);
      
      // Reset form
      setVehiclePhotos([]);
      setVehiclePhotoPreviews([]);
      setBrand('');
      setModel('');
      setColor('');
      setYear('');
      setVehicleType('');
      setUploadProgress(0);
      
      // Reload trainings
      await loadTrainings();
      
      // 🔥 OPTION C: Aggressives Polling + Auto-Navigation
      // Warte bis Training "ready" ist, dann öffne "Bild erstellen" Accordion
      let pollAttempts = 0;
      const maxPollAttempts = 60; // 5 Minuten (alle 5 Sekunden)
      
      const pollInterval = setInterval(async () => {
        pollAttempts++;
        console.log(`🔄 Polling training status... (attempt ${pollAttempts}/${maxPollAttempts})`);
        
        await loadTrainings();
        
        // Prüfe aktuelles Training
        const res = await apiClient.get('/api/vehicles/gallery');
        if (res.ok) {
          const data = await res.json();
          const latestTraining = data.trainings?.[0]; // Neuestes Training
          
          if (latestTraining && latestTraining.status === 'ready') {
            console.log('✅ Training ist ready! Auto-Navigation zu "Bild erstellen"');
            clearInterval(pollInterval);
            
            // 🎯 OPTION C: Auto-Navigation
            setShowWizard(false); // Wizard schließen
            setAccordionOpen(prev => ({...prev, createImage: true})); // "Bild erstellen" öffnen
            
            // Scroll zu "Bild erstellen"
            setTimeout(() => {
              const createImageSection = document.querySelector('[data-testid="create-image-section"]');
              if (createImageSection) {
                createImageSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }, 500);
            
            return;
          }
          
          if (pollAttempts >= maxPollAttempts) {
            console.warn('⏰ Polling timeout - Training dauert länger als erwartet');
            clearInterval(pollInterval);
          }
        }
      }, 5000); // Alle 5 Sekunden
      
    } catch (error) {
      console.error('Error starting training:', error);
      alert(`Fehler: ${error.message}`);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };
  
  // Helper: Wizard öffnen + nach oben scrollen
  const openWizard = (type = 'vehicle') => {
    setTrainingType(type);
    setShowWizard(true);
    
    // Scroll nach oben zum Wizard
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };
  
  // Helper: Prüfe ob alle Pflichtfelder ausgefüllt sind
  const isFormValid = () => {
    // Check Photos
    if (vehiclePhotos.length !== 10) return false;
    
    if (trainingType === 'vehicle') {
      return !!(brand && model && color);
    } else if (trainingType === 'avatar') {
      // Alle Avatar-Felder müssen ausgefüllt sein
      const basicFieldsValid = !!(avatarName && gender && ethnicity && eyeColor && hairDescription && bodyType && height);
      
      // Brustgröße nur für Frauen Pflicht
      if (gender === 'frau') {
        return basicFieldsValid && !!breastSize;
      }
      
      return basicFieldsValid;
    }
    
    return false;
  };
  
  // Helper: Gibt Liste der fehlenden Pflichtfelder zurück
  const getMissingFields = () => {
    const missing = [];
    
    if (vehiclePhotos.length !== 10) {
      missing.push(`${vehiclePhotos.length}/10 Fotos`);
    }
    
    if (trainingType === 'vehicle') {
      if (!brand) missing.push('Marke');
      if (!model) missing.push('Modell');
      if (!color) missing.push('Farbe');
    } else if (trainingType === 'avatar') {
      if (!avatarName) missing.push('Name');
      if (!gender) missing.push('Geschlecht');
      // Age removed from wizard
      if (!ethnicity) missing.push('Ethnie');
      if (!eyeColor) missing.push('Augenfarbe');
      if (!hairDescription) missing.push('Frisur');
      if (!bodyType) missing.push('Körpertyp');
      if (!height) missing.push('Körpergröße');
      if (gender === 'frau' && !breastSize) missing.push('Brustgröße');
    }
    
    return missing;
  };
  
  const handleGenerate = async () => {
    if (!currentTraining || currentTraining.status !== 'ready') {
      alert('Training ist noch nicht fertig!');
      return;
    }
    
    if (!prompt) {
      alert('Bitte Szene beschreiben!');
      return;
    }
    
    setIsGenerating(true);
    
    try {
      const res = await apiClient.post('/api/vehicles/generate', {
        training_id: currentTraining.id,
        prompt,
        num_images: numImages,
        time_of_day: timeOfDay,
        weather,
        camera,
        style,
        image_format: imageFormat
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Generation fehlgeschlagen');
      }
      
      const data = await res.json();
      console.log('✅ Bilder generiert:', data);
      
      // Reload gallery
      await loadGenerations();
      
      // Reset prompt
      setPrompt('');
      
      // Auto-Scroll zur Galerie
      setTimeout(() => {
        const galleryElement = document.querySelector('[data-testid="vehicle-gallery"]');
        if (galleryElement) {
          galleryElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);
      
    } catch (error) {
      console.error('Error generating:', error);
      alert(`Fehler: ${error.message}`);
    } finally {
      setIsGenerating(false);
    }
  };
  
  const handleModifyImage = async (imageUrl, trainingIdOrGenerationId) => {
    console.log('🎨 Opening modify modal:', { imageUrl, trainingIdOrGenerationId });
    
    // Check if it's a generation ID first
    const generation = generations.find(g => g.id === trainingIdOrGenerationId);
    
    let trainingId;
    if (generation) {
      // It's a generation ID - use training_id from generation
      trainingId = generation.training_id || currentTraining?.id;
    } else {
      // It's directly a training ID (from training photos)
      trainingId = trainingIdOrGenerationId;
    }
    
    if (!trainingId) {
      alert('Fehler: Training-ID nicht gefunden');
      return;
    }
    
    console.log('   Training ID:', trainingId);
    
    setModifySourceImage(imageUrl);
    setModifyTrainingId(trainingId);
    setShowModifyModal(true);
  };
  
  const handleModifySubmit = async (modifyPrompt, numImages = 1, selectedOptions = []) => {
    if (!modifySourceImage || !modifyTrainingId) {
      alert('Fehler: Kein Bild ausgewählt');
      return;
    }
    
    setIsModifying(true);
    
    try {
      console.log('🔧 Modifying image:', {
        trainingId: modifyTrainingId,
        imageUrl: modifySourceImage,
        prompt: modifyPrompt,
        numImages: numImages,
        selectedOptions: selectedOptions
      });
      
      // Create FormData for the request
      const formData = new FormData();
      formData.append('training_id', modifyTrainingId);
      formData.append('source_image_url', modifySourceImage);
      formData.append('modification_prompt', modifyPrompt);
      formData.append('num_images', numImages.toString());
      // Send selected options as JSON metadata
      if (selectedOptions.length > 0) {
        formData.append('modification_tags', JSON.stringify(selectedOptions.map(opt => opt.label)));
      }
      
      const token = localStorage.getItem('snapshot_token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      
      const response = await fetch(`${backendUrl}/api/vehicles/modify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Modifizierung fehlgeschlagen');
      }
      
      const data = await response.json();
      console.log('✅ Bild modifiziert:', data);
      
      // Close modal
      setShowModifyModal(false);
      setModifySourceImage(null);
      setModifyTrainingId(null);
      
      // Reload gallery
      await loadGenerations();
      
      // Success feedback
      alert('✅ Modifizierung erfolgreich! Neues Bild wurde erstellt.');
      
      // Auto-Scroll zur Galerie
      setTimeout(() => {
        const galleryElement = document.querySelector('[data-testid="vehicle-gallery"]');
        if (galleryElement) {
          galleryElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);
      
    } catch (error) {
      console.error('Error modifying:', error);
      alert(`Fehler: ${error.message}`);
    } finally {
      setIsModifying(false);
    }
  };
  
  const handleUseTemplate = (template) => {
    setPrompt(template.full);
    setShowTemplates(false);
    setAccordionOpen(prev => ({...prev, createImage: true, history: false}));
    
    // Scroll to prompt field
    setTimeout(() => {
      const promptField = document.querySelector('[data-testid="vehicle-prompt-textarea"]');
      if (promptField) {
        promptField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        promptField.focus();
      }
    }, 300);
  };
  
  const handleDeleteTraining = async (trainingId, trainingBrand, trainingModel) => {
    // DSGVO-konforme Warnung
    const confirmed = window.confirm(
      `⚠️ ${trainingBrand} ${trainingModel} löschen?\n\n` +
      'Alle Trainings-Daten und generierten Bilder werden permanent gelöscht.\n\n' +
      'Diese Aktion kann nicht rückgängig gemacht werden.\n\n' +
      'Wirklich löschen?'
    );
    
    if (!confirmed) {
      console.log('❌ User cancelled training deletion');
      return;
    }
    
    try {
      console.log('🗑️ Deleting vehicle training:', trainingId);
      
      const res = await apiClient.delete(`/api/vehicles/training/${trainingId}`);
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Löschen fehlgeschlagen');
      }
      
      const result = await res.json();
      console.log('✅ Training deleted:', result);
      
      // Reload trainings
      await loadTrainings();
      await loadGenerations();
      
      // Switch to first training or show empty state
      if (trainings.length > 1) {
        setActiveTrainingIndex(0);
      }
      
    } catch (error) {
      console.error('❌ Delete training failed:', error);
      alert(`Fehler beim Löschen: ${error.message}`);
    }
  };
  
  return (
    <div className="min-h-screen bg-[#0A0E1A] relative text-white">
      
      {/* Animierte Hintergrund-Blobs (Luma-Style) - Mobile optimiert */}
      <div className="fixed inset-0 pointer-events-none z-0">
        {/* Blob 1 - Reduziert auf Mobile */}
        <div className="absolute top-0 -left-40 w-64 h-64 md:w-96 md:h-96 bg-gradient-to-br from-teal-500/10 via-cyan-500/8 to-blue-500/5 md:from-teal-500/20 md:via-cyan-500/15 md:to-blue-500/10 rounded-full blur-3xl animate-float" />
        
        {/* Blob 2 - Kleiner auf Mobile */}
        <div className="absolute bottom-0 -right-40 w-96 h-96 md:w-[600px] md:h-[600px] bg-gradient-to-br from-purple-500/8 via-blue-500/5 to-cyan-500/3 md:from-purple-500/15 md:via-blue-500/10 md:to-cyan-500/5 rounded-full blur-3xl animate-float-delayed" />
        
        {/* Blob 3 - Versteckt auf Mobile, nur Desktop */}
        <div className="hidden md:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-gradient-to-br from-cyan-400/10 to-teal-400/5 rounded-full blur-2xl animate-pulse" />
      </div>
      
      {/* Content Layer */}
      <div className="relative z-10">
        <AppHeader menuOpen={menuOpen} onMenuToggle={() => setMenuOpen(!menuOpen)} />
        
        {menuOpen && (
          <>
            <div 
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-30"
              onClick={() => setMenuOpen(false)}
            />
            
            {/* Slide-In Menu */}
            <div className="fixed top-0 right-0 h-full w-80 bg-black/95 backdrop-blur-xl border-l border-white/20 z-40 p-6 overflow-y-auto">
              
              {/* Menu Header */}
              <div className="flex items-center justify-between mb-8 pb-4 border-b border-white/10">
                <h3 className="text-white font-bold text-lg">Menü</h3>
                <button
                  onClick={() => setMenuOpen(false)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-all"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
              
              {/* Navigation Links */}
              <div className="space-y-3">
                
                {/* Meine ID Card Link */}
                <Link
                  to="/"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 text-white hover:text-cyan-400 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-all"
                >
                  <Home className="w-5 h-5" />
                  <span className="font-medium">Meine ID Card</span>
                </Link>
                
                {/* Vehicles - Current Page */}
                <div className="flex items-center gap-3 px-4 py-3 text-cyan-400 bg-cyan-500/20 border-2 border-cyan-500 rounded-xl">
                  <Car className="w-5 h-5" />
                  <span className="font-bold">Mein Studio</span>
                </div>
                
                {/* Logout */}
                <button
                  onClick={() => {
                    localStorage.removeItem('snapshot_token');
                    window.location.href = '/welcome';
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-white hover:text-red-400 bg-white/5 hover:bg-red-500/10 border border-white/10 hover:border-red-500/30 rounded-xl transition-all"
                >
                  <X className="w-5 h-5" />
                  <span className="font-medium">Abmelden</span>
                </button>
                
              </div>
              
            </div>
          </>
        )}
      
      <div className="max-w-6xl mx-auto px-4 py-24 md:py-32">
        
        {/* Header - Luma Style - immer sichtbar */}
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4 tracking-tight font-['Poppins']">
            SNAPSHOT STUDIO
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto font-['Inter']">
            Wo deine Ideen Gestalt annehmen
          </p>
        </div>
        
        {/* Empty State - NUR wenn Loading fertig UND wirklich keine Trainings */}
        {!isLoadingTrainings && !hasTraining && !showWizard && (
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-12 mb-8 text-center shadow-2xl shadow-black/20">
            <div className="w-28 h-28 bg-white/10 backdrop-blur-2xl border border-white/20 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-white/10">
              <Car className="w-14 h-14 text-white drop-shadow-[0_0_12px_rgba(255,255,255,0.5)]" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-3">Kein Vehicle trainiert</h2>
            <p className="text-gray-300 mb-8 max-w-md mx-auto leading-relaxed">
              Lade 10 Fotos deines Vehicles hoch (Auto, Motorrad, Bike). Training dauert 7-10 Minuten.
            </p>
            <button
              onClick={() => openWizard('vehicle')}
              className="px-10 py-4 bg-gradient-to-r from-cyan-500 to-teal-500 text-white rounded-full font-semibold hover:shadow-xl hover:shadow-cyan-500/40 hover:scale-105 transition-all duration-300 flex items-center gap-3 mx-auto shadow-lg shadow-cyan-500/30"
            >
              <Sparkles className="w-5 h-5" />
              Jetzt trainieren
            </button>
          </div>
        )}
        
        {/* Training in Progress */}
        {currentTraining && currentTraining.status === 'training' && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-8 mb-8 text-center">
            <Loader2 className="w-16 h-16 text-[#10B981] mx-auto mb-4 animate-spin" />
            <h2 className="text-2xl font-bold text-white mb-3">Training läuft...</h2>
            <p className="text-white/70 mb-4">
              Dein {getTrainingDisplayName(currentTraining)} wird trainiert
            </p>
            <p className="text-white/50 text-xs mt-4">Dauert ca. 5-8 Minuten</p>
          </div>
        )}
        
        {/* Wizard - Luma Glassmorphism */}
        {showWizard && (
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 mb-8 shadow-2xl shadow-black/20">
            <h2 className="text-3xl font-bold text-white mb-8">
              {trainingType === 'vehicle' ? 'Vehicle-Details' : 'Avatar-Details'}
            </h2>
            
            {/* Bedingte Formular-Felder */}
            {trainingType === 'vehicle' ? (
              // VEHICLE FORM
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Marke *</label>
                  <input
                    type="text"
                    value={brand}
                    onChange={(e) => setBrand(e.target.value)}
                    placeholder="z.B. Porsche, MAN, Ducati, Canyon"
                    className="w-full px-5 py-4 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl text-white placeholder:text-gray-500 focus:border-cyan-400/50 focus:ring-2 focus:ring-cyan-400/20 transition-all"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Modell *</label>
                  <input
                    type="text"
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    placeholder="z.B. 911, Actros, Monster, Aeroad"
                    className="w-full px-5 py-4 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl text-white placeholder:text-gray-500 focus:border-cyan-400/50 focus:ring-2 focus:ring-cyan-400/20 transition-all"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Farbe *</label>
                  <input
                    type="text"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    placeholder="z.B. Rot, Schwarz, Weiß"
                    className="w-full px-5 py-4 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl text-white placeholder:text-gray-500 focus:border-cyan-400/50 focus:ring-2 focus:ring-cyan-400/20 transition-all"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Baujahr</label>
                  <input
                    type="text"
                    value={year}
                    onChange={(e) => setYear(e.target.value)}
                    placeholder="z.B. 2022"
                    className="w-full px-5 py-4 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl text-white placeholder:text-gray-500 focus:border-cyan-400/50 focus:ring-2 focus:ring-cyan-400/20 transition-all"
                  />
                </div>
              </div>
            ) : (
              // AVATAR FORM
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Name *</label>
                  <input
                    type="text"
                    value={avatarName}
                    onChange={(e) => setAvatarName(e.target.value)}
                    placeholder="z.B. Max, Anna, Sarah"
                    className="w-full px-5 py-4 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl text-white placeholder:text-gray-500 focus:border-purple-400/50 focus:ring-2 focus:ring-purple-400/20 transition-all"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Geschlecht *</label>
                  <select
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                    className="w-full px-5 py-4 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl text-white focus:border-purple-400/50 focus:ring-2 focus:ring-purple-400/20 transition-all"
                  >
                    <option value="">Bitte wählen</option>
                    <option value="mann">Mann</option>
                    <option value="frau">Frau</option>
                    <option value="kind">Kind</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Ethnie *</label>
                  <select
                    value={ethnicity}
                    onChange={(e) => setEthnicity(e.target.value)}
                    className="w-full px-5 py-4 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl text-white focus:border-purple-400/50 focus:ring-2 focus:ring-purple-400/20 transition-all"
                  >
                    <option value="">Bitte wählen</option>
                    <option value="caucasian">Europäisch</option>
                    <option value="latina">Latina</option>
                    <option value="asian">Asiatisch</option>
                    <option value="black">Schwarz</option>
                    <option value="middle eastern">Arabisch</option>
                    <option value="central asian">Kasachisch</option>
                    <option value="indian">Indisch</option>
                    <option value="other">Sonstige</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Augenfarbe *</label>
                  <select
                    value={eyeColor}
                    onChange={(e) => setEyeColor(e.target.value)}
                    className="w-full px-5 py-4 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl text-white focus:border-purple-400/50 focus:ring-2 focus:ring-purple-400/20 transition-all"
                  >
                    <option value="">Bitte wählen</option>
                    <option value="brown eyes">Braun</option>
                    <option value="blue eyes">Blau</option>
                    <option value="green eyes">Grün</option>
                    <option value="grey eyes">Grau</option>
                    <option value="dark eyes">Schwarz</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Frisur *</label>
                  <select
                    value={hairDescription}
                    onChange={(e) => setHairDescription(e.target.value)}
                    className="w-full px-5 py-4 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl text-white focus:border-purple-400/50 focus:ring-2 focus:ring-purple-400/20 transition-all"
                  >
                    <option value="">Bitte wählen</option>
                    <option value="short hair">Kurze Haare</option>
                    <option value="long hair">Lange Haare</option>
                    <option value="shoulder-length hair">Schulterlang</option>
                    <option value="bald">Glatze</option>
                    <option value="curly hair">Lockig</option>
                    <option value="straight hair">Glatt</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Körpertyp *</label>
                  <select
                    value={bodyType}
                    onChange={(e) => setBodyType(e.target.value)}
                    className="w-full px-5 py-4 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl text-white focus:border-purple-400/50 focus:ring-2 focus:ring-purple-400/20 transition-all"
                  >
                    <option value="">Bitte wählen</option>
                    <option value="petite body">Zierlich</option>
                    <option value="slim body">Schlank</option>
                    <option value="athletic body">Athletisch</option>
                    <option value="curvy body">Kurvig</option>
                    <option value="voluptuous body">Üppig</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Körpergröße *</label>
                  <select
                    value={height}
                    onChange={(e) => setHeight(e.target.value)}
                    className="w-full px-5 py-4 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl text-white focus:border-purple-400/50 focus:ring-2 focus:ring-purple-400/20 transition-all"
                  >
                    <option value="">Bitte wählen</option>
                    <option value="short">Klein</option>
                    <option value="medium height">Mittel</option>
                    <option value="tall">Groß</option>
                  </select>
                </div>
                
                {/* Brustgröße - NUR für Frauen */}
                {gender === 'frau' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Brustgröße *</label>
                    <select
                      value={breastSize}
                      onChange={(e) => setBreastSize(e.target.value)}
                      className="w-full px-5 py-4 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl text-white focus:border-purple-400/50 focus:ring-2 focus:ring-purple-400/20 transition-all"
                    >
                      <option value="">Bitte wählen</option>
                      <option value="flat">Flach</option>
                      <option value="small">Klein</option>
                      <option value="medium">Mittel</option>
                      <option value="large">Groß</option>
                      <option value="very large">Sehr groß</option>
                    </select>
                  </div>
                )}
              </div>
            )}
            
            {/* Photo Upload */}
            <div className="mb-6">
              <label className="block text-lg font-bold text-white mb-2">
                Fotos hochladen (10 Stück)
              </label>
              
              <div className="mb-6 p-5 bg-cyan-500/5 backdrop-blur border border-cyan-500/20 rounded-2xl">
                <p className="text-gray-200 text-sm mb-3 font-medium">📸 Tipps für beste Ergebnisse:</p>
                <ul className="text-gray-400 text-xs space-y-2 list-disc list-inside">
                  {trainingType === 'vehicle' ? (
                    <>
                      <li>Verschiedene Winkel (Front, Seite, Heck, 3/4-Ansicht)</li>
                      <li>Neutraler Hintergrund bevorzugt</li>
                      <li>Gutes Tageslicht</li>
                      <li>Sauberes Vehicle (Auto/Motorrad/Bike)</li>
                    </>
                  ) : (
                    <>
                      <li>Verschiedene Winkel und Gesichtsausdrücke</li>
                      <li>Gute Beleuchtung (Tageslicht bevorzugt)</li>
                      <li>Neutraler Hintergrund</li>
                      <li>Nahaufnahmen & Ganzkörper-Aufnahmen mischen</li>
                      <li>Verschiedene Kleidung und Settings</li>
                    </>
                  )}
                </ul>
              </div>
              
              {vehiclePhotoPreviews.length === 0 ? (
                loadingPreviews ? (
                  // Loading Spinner während Preview-Generierung
                  <div className="flex flex-col items-center justify-center w-full h-64 border-2 border-dashed border-cyan-400/40 rounded-2xl bg-cyan-500/5 backdrop-blur">
                    <svg className="animate-spin h-12 w-12 text-cyan-400 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-cyan-400 font-medium">Lade {vehiclePhotos.length} Bilder...</span>
                    <span className="text-gray-400 text-sm mt-2">Einen Moment bitte</span>
                  </div>
                ) : (
                  // Upload Button
                  <label className="flex flex-col items-center justify-center w-full h-64 border-2 border-dashed border-white/20 rounded-2xl cursor-pointer hover:border-white/40 hover:bg-white/5 bg-white/[0.02] backdrop-blur transition-all duration-300">
                  <div className="w-16 h-16 bg-white/10 backdrop-blur-xl border border-white/20 rounded-full flex items-center justify-center mb-4">
                    <Upload className="w-8 h-8 text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]" />
                  </div>
                  <span className="text-white font-medium">10 Fotos hochladen</span>
                  <span className="text-gray-400 text-sm mt-2">JPG, PNG</span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoUpload}
                    className="hidden"
                  />
                  </label>
                )
              ) : (
                <div>
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-2">
                    {vehiclePhotoPreviews.map((preview, idx) => (
                      <img key={idx} src={preview} alt={`Foto ${idx+1}`} className="w-full h-24 object-cover rounded-lg" />
                    ))}
                  </div>
                  <button 
                    onClick={() => { setVehiclePhotos([]); setVehiclePhotoPreviews([]); }} 
                    className="w-full px-4 py-3 bg-red-500/20 backdrop-blur border border-red-500/30 text-red-300 rounded-2xl hover:bg-red-500/30 transition-all"
                  >
                    Fotos entfernen
                  </button>
                  <p className="text-white drop-shadow-[0_0_8px_rgba(6,182,212,0.6)] text-sm mt-3 font-medium">✓ {vehiclePhotos.length} Fotos hochgeladen</p>
                </div>
              )}
            </div>
            
            {/* Live-Validierung: Fehlende Pflichtfelder anzeigen */}
            {!isFormValid() && getMissingFields().length > 0 && (
              <div className="mb-6 p-4 bg-yellow-500/10 backdrop-blur border border-yellow-500/30 rounded-2xl">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">⚠️</span>
                  <div>
                    <p className="text-yellow-300 font-semibold text-sm mb-2">Noch benötigt:</p>
                    <p className="text-yellow-200/90 text-sm">
                      {getMissingFields().join(', ')}
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Action Buttons */}
            <div className="flex gap-4">
              <button
                onClick={() => setShowWizard(false)}
                disabled={uploading}
                className="px-8 py-4 bg-white/10 backdrop-blur border border-white/20 text-white rounded-full hover:bg-white/20 disabled:opacity-50 transition-all font-medium"
              >
                Abbrechen
              </button>
              <button
                onClick={() => {
                  console.log('🔍 Training starten geklickt');
                  handleStartTraining();
                }}
                disabled={uploading || !isFormValid()}
                className={`flex-1 py-4 px-6 rounded-full font-bold text-base flex items-center justify-center gap-2 transition-all duration-300 ${
                  uploading || !isFormValid()
                    ? 'bg-white/10 text-white/40 cursor-not-allowed border border-white/10'
                    : 'bg-gradient-to-r from-cyan-400 via-cyan-500 to-teal-500 text-black hover:shadow-2xl hover:shadow-cyan-400/60 hover:scale-[1.02] shadow-xl shadow-cyan-500/50 border-2 border-cyan-400/50'
                }`}
                data-testid="start-training-button"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" /> 
                    Hochladen... {uploadProgress}%
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 drop-shadow-[0_0_6px_rgba(0,0,0,0.4)]" /> 
                    Training starten
                  </>
                )}
              </button>
            </div>
            
            {/* Upload Progress Bar - Luma Style */}
            {uploading && uploadProgress > 0 && (
              <div className="mt-6">
                <div className="bg-white/5 backdrop-blur rounded-full h-3 overflow-hidden border border-white/10">
                  <div 
                    className="bg-gradient-to-r from-cyan-500 to-teal-500 h-full transition-all duration-300 shadow-lg shadow-cyan-500/50"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <p className="text-white drop-shadow-[0_0_8px_rgba(6,182,212,0.6)] text-sm text-center mt-2 font-medium">
                  Bilder werden hochgeladen... {uploadProgress}%
                </p>
              </div>
            )}
          </div>
        )}
        
        {/* ACCORDION SYSTEM - Luma Glassmorphism */}
        {hasTraining && currentTraining && (
          <div className="space-y-4 mb-8 mt-12">
            
            {/* Handle-Strich (Dekorativ, wie im IDCardDashboard) */}
            <div className="h-1 w-16 bg-gradient-to-r from-cyan-500 to-teal-500 rounded-full mx-auto mb-8 shadow-lg shadow-cyan-500/50" />
            
            {/* ACCORDION 1: Vehikel (Meine Vehicles - jetzt an Position 1!) */}
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl overflow-hidden shadow-2xl shadow-black/20" data-testid="create-image-section">
              {/* Accordion Header */}
              <button
                onClick={() => setAccordionOpen(prev => ({...prev, createImage: !prev.createImage}))}
                className="w-full flex items-center justify-between p-6 hover:bg-white/5 transition-all"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-500/20 to-teal-500/20 backdrop-blur border border-cyan-500/30 rounded-2xl flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-cyan-400 drop-shadow-[0_0_8px_rgba(6,182,212,0.6)]" />
                  </div>
                  <div className="text-left">
                    <h3 className="text-white font-bold text-xl">Bild erstellen</h3>
                    <p className="text-cyan-400/80 text-sm">
                      {accordionOpen.createImage ? (currentTraining?.training_type === 'avatar' ? 'Dein Avatar in jeder Szene' : 'Dein Vehicle in jeder Szene') : 'Klicken zum Öffnen'}
                    </p>
                  </div>
                </div>
                {accordionOpen.createImage ? (
                  <ChevronDown className="w-6 h-6 text-cyan-400 transform rotate-180 transition-transform" />
                ) : (
                  <ChevronDown className="w-6 h-6 text-white/50" />
                )}
              </button>
              
              {/* Accordion Content */}
              {accordionOpen.createImage && (
                <div className="border-t border-white/10 p-6 space-y-6">
                  
                  {/* Vehicle Selector Dropdown */}
                  <div className="space-y-2">
                    <label className="text-white text-sm font-medium">
                      {currentTraining?.training_type === 'avatar' ? 'Wähle deinen Avatar' : 'Wähle dein Fahrzeug'}
                    </label>
                    <select
                      value={activeTrainingIndex}
                      onChange={(e) => setActiveTrainingIndex(parseInt(e.target.value))}
                      className="w-full p-4 bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl text-white font-medium focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all appearance-none cursor-pointer"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2306b6d4'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 1rem center',
                        backgroundSize: '1.5em'
                      }}
                    >
                      {trainings.map((training, index) => (
                        <option key={training.id} value={index} className="bg-gray-900">
                          {getTrainingDisplayName(training)} ({getTrainingSubtitle(training)}) 
                          {training.status === 'ready' ? ' ✓' : training.status === 'training' ? ' ⏳' : ''}
                        </option>
                      ))}
                    </select>
                    <p className="text-cyan-400/70 text-xs">
                      {currentTraining?.training_type === 'avatar' ? 'Wähle den Avatar aus, den du in Szene setzen möchtest' : 'Wähle das Fahrzeug aus, das du in Szene setzen möchtest'}
                    </p>
                  </div>

                  {/* Avatar-Details Panel (nur für Avatare) */}
                  {currentTraining?.training_type === 'avatar' && (
                    <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 backdrop-blur-xl border border-purple-400/20 rounded-2xl overflow-hidden">
                      {/* Panel Header */}
                      <button
                        onClick={() => setAccordionOpen(prev => ({...prev, avatarDetails: !prev.avatarDetails}))}
                        className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-all"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-purple-500/20 backdrop-blur border border-purple-400/30 rounded-lg flex items-center justify-center">
                            <span className="text-lg">ℹ️</span>
                          </div>
                          <span className="text-white font-semibold text-sm">Avatar-Details anzeigen</span>
                        </div>
                        {accordionOpen.avatarDetails ? (
                          <ChevronDown className="w-5 h-5 text-purple-400 transform rotate-180 transition-transform" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-white/50" />
                        )}
                      </button>
                      
                      {/* Panel Content */}
                      {accordionOpen.avatarDetails && (
                        <div className="border-t border-purple-400/20 p-4 space-y-3">
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <p className="text-purple-300 font-medium mb-1">Name</p>
                              <p className="text-white">{currentTraining.name || '-'}</p>
                            </div>
                            <div>
                              <p className="text-purple-300 font-medium mb-1">Geschlecht</p>
                              <p className="text-white">{currentTraining.gender || '-'}</p>
                            </div>
                            <div>
                              <p className="text-purple-300 font-medium mb-1">Alter</p>
                              <p className="text-white">{currentTraining.age || '-'}</p>
                            </div>
                            <div>
                              <p className="text-purple-300 font-medium mb-1">Ethnie</p>
                              <p className="text-white">{currentTraining.ethnicity || '-'}</p>
                            </div>
                            <div>
                              <p className="text-purple-300 font-medium mb-1">Augenfarbe</p>
                              <p className="text-white">{currentTraining.eye_color || '-'}</p>
                            </div>
                            <div>
                              <p className="text-purple-300 font-medium mb-1">Frisur</p>
                              <p className="text-white">{currentTraining.hair_description || '-'}</p>
                            </div>
                            <div>
                              <p className="text-purple-300 font-medium mb-1">Körpertyp</p>
                              <p className="text-white">{currentTraining.body_type || '-'}</p>
                            </div>
                            <div>
                              <p className="text-purple-300 font-medium mb-1">Körpergröße</p>
                              <p className="text-white">{currentTraining.height || '-'}</p>
                            </div>
                            {currentTraining.gender === 'frau' && currentTraining.breast_size && (
                              <div>
                                <p className="text-purple-300 font-medium mb-1">Brustgröße</p>
                                <p className="text-white">{currentTraining.breast_size}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Vorlagen Button */}
                  <button
                    onClick={() => setShowTemplatesModal(true)}
                    className="w-full py-4 px-5 bg-gradient-to-br from-white/10 to-white/5 hover:from-cyan-500/20 hover:to-teal-500/10 backdrop-blur-sm border border-cyan-500/30 hover:border-cyan-500/50 rounded-xl font-semibold text-white text-base transition-all flex items-center justify-center gap-2 shadow-lg"
                    data-testid="open-templates-button"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                    </svg>
                    Keine Idee? Nutze unsere kostenlosen Vorlagentexte
                  </button>

                  
                  {/* Prompt Input - Luma Style */}
                  <div>
                    <label className="block text-lg font-semibold text-white mb-3">Szene beschreiben</label>
                    <textarea
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      placeholder={currentTraining?.training_type === 'avatar' 
                        ? "✨ Beschreibe deine Vision detailliert! Z.B. Ich am Hafen von Monaco sitzend, schwarzes Outfit, Champagnerglas in der Hand, Luxusyachten im Hintergrund..." 
                        : "z.B. auf einer Bergstraße in den Alpen, Sonnenuntergang, dramatische Landschaft..."
                      }
                      className="w-full px-5 py-4 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl text-white placeholder:text-gray-500 focus:border-cyan-400/50 focus:outline-none transition-colors duration-150 resize-none"
                      rows={4}
                      data-testid="vehicle-prompt-textarea"
                    />
                    <p className="text-gray-400 text-xs mt-3">
                      💡 Tipp: Lass dich von unseren Textvorlagen inspirieren – oder kreiere einfach frei!
                    </p>
                    
                    {/* Live Prompt-Vorschau */}
                    {prompt && (
                      <div className="mt-4 p-4 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 backdrop-blur-xl border border-cyan-400/20 rounded-2xl">
                        <div className="flex items-center gap-2 mb-3">
                          <Sparkles className="w-4 h-4 text-cyan-400" />
                          <h3 className="text-sm font-semibold text-cyan-400">Finaler Prompt wird sein:</h3>
                        </div>
                        
                        {/* User Text */}
                        <div className="mb-3">
                          <p className="text-xs font-medium text-gray-400 mb-1">Dein Text:</p>
                          <p className="text-sm text-white/90 italic">"{prompt}"</p>
                          <p className="text-xs text-cyan-400/60 mt-1">→ Wird automatisch ins Englische übersetzt</p>
                        </div>
                        
                        {/* Automatisch hinzugefügte Keywords */}
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-gray-400">Automatisch hinzugefügt:</p>
                          
                          {/* Tageszeit */}
                          <div className="flex items-start gap-2">
                            <span className="text-lg">🌅</span>
                            <div className="flex-1">
                              <p className="text-xs font-medium text-cyan-300">{getLabelForValue('timeOfDay', timeOfDay)}</p>
                              <p className="text-xs text-gray-300 leading-relaxed">{getKeywordsForValue('timeOfDay', timeOfDay)}</p>
                            </div>
                          </div>
                          
                          {/* Wetter */}
                          <div className="flex items-start gap-2">
                            <span className="text-lg">☀️</span>
                            <div className="flex-1">
                              <p className="text-xs font-medium text-cyan-300">{getLabelForValue('weather', weather)}</p>
                              <p className="text-xs text-gray-300 leading-relaxed">{getKeywordsForValue('weather', weather)}</p>
                            </div>
                          </div>
                          
                          {/* Kamera */}
                          <div className="flex items-start gap-2">
                            <span className="text-lg">📷</span>
                            <div className="flex-1">
                              <p className="text-xs font-medium text-cyan-300">{getLabelForValue('camera', camera)}</p>
                              <p className="text-xs text-gray-300 leading-relaxed">{getKeywordsForValue('camera', camera)}</p>
                            </div>
                          </div>
                          
                          {/* Bildstil */}
                          <div className="flex items-start gap-2">
                            <span className="text-lg">🎨</span>
                            <div className="flex-1">
                              <p className="text-xs font-medium text-cyan-300">{getLabelForValue('style', style)}</p>
                              <p className="text-xs text-gray-300 leading-relaxed">{getKeywordsForValue('style', style)}</p>
                            </div>
                          </div>
                          
                          {/* Bildformat */}
                          <div className="flex items-start gap-2">
                            <span className="text-lg">🖼️</span>
                            <div className="flex-1">
                              <p className="text-xs font-medium text-cyan-300">Format</p>
                              <p className="text-xs text-gray-300 leading-relaxed">{IMAGE_FORMAT_LABELS[imageFormat]}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* V6 Dropdowns */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-4">Einstellungen</label>
                    <V6ModernPicker
                      timeOfDay={timeOfDay}
                      setTimeOfDay={setTimeOfDay}
                      weather={weather}
                      setWeather={setWeather}
                      camera={camera}
                      setCamera={setCamera}
                      style={style}
                      setStyle={setStyle}
                      imageFormat={imageFormat}
                      setImageFormat={setImageFormat}
                      substyle={substyle}
                      setSubstyle={setSubstyle}
                    />
                  </div>
                  
                  {/* Number of Images - Luma Style */}
                  <div className="flex items-center justify-between p-4 bg-white/5 backdrop-blur border border-white/10 rounded-2xl">
                    <label className="text-sm font-medium text-gray-300">Anzahl Bilder</label>
                    <select
                      value={numImages}
                      onChange={(e) => setNumImages(parseInt(e.target.value))}
                      className="px-5 py-2.5 bg-white/10 backdrop-blur border border-white/20 rounded-full text-white font-medium focus:border-cyan-400/50 focus:outline-none transition-colors duration-150 cursor-pointer"
                    >
                      <option value={1} className="bg-[#1A1A1A]">1 Bild</option>
                      <option value={2} className="bg-[#1A1A1A]">2 Bilder</option>
                      <option value={3} className="bg-[#1A1A1A]">3 Bilder</option>
                      <option value={4} className="bg-[#1A1A1A]">4 Bilder</option>
                    </select>
                  </div>
                  
                  {/* Generate Button - Luma Pill Style */}
                  <button
                    onClick={handleGenerate}
                    disabled={isGenerating || !prompt}
                    className={`w-full py-5 rounded-full font-semibold text-lg flex items-center justify-center gap-3 transition-all duration-300 ${
                      isGenerating
                        ? 'bg-gradient-to-r from-cyan-500/50 via-teal-500/50 to-cyan-500/50 bg-[length:200%_100%] animate-[gradient_3s_ease_infinite] text-white shadow-xl shadow-cyan-500/50 cursor-wait'
                        : !prompt
                        ? 'bg-white/10 text-white/40 cursor-not-allowed'
                        : 'bg-gradient-to-r from-cyan-500 to-teal-500 text-white hover:shadow-xl hover:shadow-cyan-500/40 hover:scale-105 shadow-lg shadow-cyan-500/30'
                    }`}
                    data-testid="vehicle-generate-button"
                  >
                    {isGenerating ? (
                      <>
                        <svg className="animate-spin h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="font-bold">KI erstellt {numImages} {numImages === 1 ? 'Bild' : 'Bilder'}...</span>
                        <span className="text-cyan-200 text-sm animate-pulse">⚡ Dauert ca. 30-60 Sek</span>
                      </>
                    ) : (
                      <><Sparkles className="w-6 h-6" /> {numImages} {numImages === 1 ? 'Bild' : 'Bilder'} generieren</>
                    )}
                  </button>
                  
                </div>
              )}
            </div>
            
            {/* ACCORDION 2: History & Vorlagen */}
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl overflow-hidden shadow-2xl shadow-black/20">
              {/* Accordion Header */}
              <button
                onClick={() => setAccordionOpen(prev => ({...prev, history: !prev.history}))}
                className="w-full flex items-center justify-between p-6 hover:bg-white/5 transition-all"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-500/20 to-teal-500/20 backdrop-blur border border-cyan-500/30 rounded-2xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <h3 className="text-white font-bold text-xl">History & Vorlagen</h3>
                    <p className="text-cyan-400/80 text-sm">
                      {accordionOpen.history ? 'Deine letzten Prompts & Ideen' : 'Klicken zum Öffnen'}
                    </p>
                  </div>
                </div>
                {accordionOpen.history ? (
                  <ChevronDown className="w-6 h-6 text-cyan-400 transform rotate-180 transition-transform" />
                ) : (
                  <ChevronDown className="w-6 h-6 text-white/50" />
                )}
              </button>
              
              {/* Accordion Content */}
              {accordionOpen.history && (
                <div className="border-t border-white/10 p-6">
                  <div className="grid grid-cols-2 gap-4">
                    {/* History Button */}
                    <button
                      onClick={() => {
                        alert('History-Feature kommt bald! 🚧');
                      }}
                      className="w-full py-4 px-5 bg-gradient-to-br from-white/10 to-white/5 hover:from-cyan-500/20 hover:to-teal-500/10 backdrop-blur-sm border border-cyan-500/30 hover:border-cyan-500/50 rounded-xl font-semibold text-white text-base transition-all flex items-center justify-center gap-2 shadow-lg"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      History
                    </button>
                    
                    {/* Vorlagen Button */}
                    <button
                      onClick={() => setShowTemplatesModal(true)}
                      className="w-full py-4 px-5 bg-gradient-to-br from-white/10 to-white/5 hover:from-cyan-500/20 hover:to-teal-500/10 backdrop-blur-sm border border-cyan-500/30 hover:border-cyan-500/50 rounded-xl font-semibold text-white text-base transition-all flex items-center justify-center gap-2 shadow-lg"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                      </svg>
                      Vorlagen
                    </button>
                  </div>
                </div>
              )}
            </div>
            
            {/* ACCORDION 3: Meine Fahrzeuge (Switcher) */}
            {trainings.length >= 1 && (
              <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl overflow-hidden shadow-2xl shadow-black/20">
                {/* Accordion Header */}
                <button
                  onClick={() => setAccordionOpen(prev => ({...prev, myVehicles: !prev.myVehicles}))}
                  className="w-full flex items-center justify-between p-6 hover:bg-white/5 transition-all"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-cyan-500/20 to-teal-500/20 backdrop-blur border border-cyan-500/30 rounded-2xl flex items-center justify-center">
                      <Car className="w-6 h-6 text-cyan-400 drop-shadow-[0_0_8px_rgba(6,182,212,0.6)]" />
                    </div>
                    <div className="text-left">
                      <h3 className="text-white font-bold text-xl">Details ändern</h3>
                      <p className="text-cyan-400/80 text-sm">
                        {accordionOpen.myVehicles ? `${trainings.length} trainiert` : 'Klicken zum Öffnen'}
                      </p>
                    </div>
                  </div>
                  {accordionOpen.myVehicles ? (
                    <ChevronDown className="w-6 h-6 text-cyan-400 transform rotate-180 transition-transform" />
                  ) : (
                    <ChevronDown className="w-6 h-6 text-white/50" />
                  )}
                </button>
                
                {/* Accordion Content */}
                {accordionOpen.myVehicles && (
                  <div className="border-t border-white/10 p-6 space-y-3">
                    
                    {/* Info Text */}
                    <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-3 mb-4">
                      <p className="text-cyan-300 text-sm text-center">
                        {currentTraining?.training_type === 'avatar' ? 'Wähle den Avatar aus, den du in Szene setzen möchtest' : 'Wähle das Fahrzeug aus, das du in Szene setzen möchtest'}
                      </p>
                    </div>
                    
                    {/* Trainings List */}
                    {trainings.map((training, index) => (
                      <div key={training.id} className="w-full">
                        {/* Vehicle Item */}
                        <div className={`w-full text-left p-4 rounded-xl transition-all ${
                          activeTrainingIndex === index
                            ? 'bg-cyan-500/20 border-2 border-cyan-500'
                            : 'bg-white/5 border-2 border-white/10 hover:bg-white/10'
                        }`}>
                          <div className="flex items-center gap-3">
                            {/* Status Indicator */}
                            <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                              training.status === 'ready' ? 'bg-cyan-400 shadow-lg shadow-cyan-400/50' :
                              training.status === 'training' ? 'bg-yellow-500 animate-pulse' :
                              'bg-white/30'
                            }`}></div>
                            
                            {/* Training Info - clickable to expand photos */}
                            <button
                              onClick={() => {
                                // Toggle submenu
                                if (expandedVehicleId === training.id) {
                                  setExpandedVehicleId(null);
                                } else {
                                  setExpandedVehicleId(training.id);
                                  setActiveTrainingIndex(index);
                                }
                              }}
                              className="flex-1 min-w-0 text-left"
                            >
                              <div className="text-white font-semibold text-sm truncate flex items-center gap-2">
                                {getTrainingDisplayName(training)}
                                {training.training_image_urls && training.training_image_urls.length > 0 && (
                                  <span className="text-cyan-400 text-xs">
                                    ({training.training_image_urls.length} Fotos)
                                  </span>
                                )}
                              </div>
                              <div className="text-white/50 text-xs">
                                {training.color} · {training.year || 'N/A'} · {
                                  training.status === 'ready' ? 'Bereit' :
                                  training.status === 'training' ? 'Training läuft...' :
                                  'Pending'
                                }
                              </div>
                              {/* 30-Tage Timer */}
                              {training.status === 'ready' && (
                                <div className="flex items-center gap-1.5 mt-1">
                                  <div className={`w-1.5 h-1.5 rounded-full ${getVehicleCountdownInfo(training).color}`}></div>
                                  <span className={`text-xs font-medium ${
                                    getVehicleCountdownInfo(training).active ? 'text-white/70' : 'text-red-400'
                                  }`}>
                                    {getVehicleCountdownInfo(training).text}
                                  </span>
                                </div>
                              )}
                            </button>
                            
                            {/* Expand/Collapse Icon */}
                            {training.training_image_urls && training.training_image_urls.length > 0 && (
                              <ChevronDown className={`w-5 h-5 text-cyan-400 transition-transform ${
                                expandedVehicleId === training.id ? 'rotate-180' : ''
                              }`} />
                            )}
                            
                            {/* Active Checkmark */}
                            {activeTrainingIndex === index && (
                              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-cyan-500 flex items-center justify-center">
                                <CheckCircle className="w-4 h-4 text-black" />
                              </div>
                            )}
                            
                            {/* Delete Button - rechts neben Content */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteTraining(training.id, training.brand, training.model);
                              }}
                              className="flex-shrink-0 p-2 bg-red-500/20 backdrop-blur-sm border border-red-500/40 rounded-lg hover:bg-red-500/40 transition-all text-red-400 hover:text-red-300"
                              title="Training löschen"
                              type="button"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        
                        {/* SUBMENU: Training Photos - Swipeable Gallery */}
                        {expandedVehicleId === training.id && training.training_image_urls && training.training_image_urls.length > 0 && (
                          <div className="mt-3 p-4 bg-white/5 border border-white/10 rounded-xl relative">
                            {/* Scroll-Hinweis: Pfeil am rechten Rand */}
                            {training.training_image_urls.length > 3 && (
                              <div className="absolute top-1/2 right-2 -translate-y-1/2 z-20 pointer-events-none">
                                <div className="bg-cyan-500/80 backdrop-blur-sm rounded-full p-2 shadow-lg animate-pulse">
                                  <ChevronRight className="w-5 h-5 text-white" />
                                </div>
                              </div>
                            )}
                            
                            {/* Horizontal Scroll Container */}
                            <div className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-hide" style={{scrollbarWidth: 'none', msOverflowStyle: 'none'}}>
                              {training.training_image_urls.map((imageUrl, photoIdx) => (
                                <div key={photoIdx} className="relative group flex-shrink-0 snap-start">
                                  <div className="w-32 h-32 md:w-24 md:h-24 rounded-lg overflow-hidden border border-white/10 bg-black/20">
                                    <img 
                                      src={imageUrl.startsWith('http') ? imageUrl : `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}${imageUrl}`}
                                      alt={`Foto ${photoIdx + 1}`}
                                      className="w-full h-full object-cover"
                                      loading="eager"
                                    />
                                  </div>
                                  
                                  {/* Modify Button on Hover */}
                                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/70 transition-all duration-200 rounded-lg flex items-center justify-center">
                                    <button
                                      onClick={() => {
                                        const imageFullUrl = imageUrl.startsWith('http') 
                                          ? imageUrl 
                                          : `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}${imageUrl}`;
                                        handleModifyImage(imageFullUrl, training.id);
                                      }}
                                      className="opacity-0 group-hover:opacity-100 transition-all duration-200 bg-gradient-to-r from-cyan-500 to-teal-500 text-white px-3 py-2 rounded-lg font-bold text-xs hover:scale-105 flex items-center gap-1.5 shadow-lg"
                                      data-testid={`modify-training-photo-${training.id}-${photoIdx}`}
                                    >
                                      <Palette className="w-3.5 h-3.5" />
                                      <span>Modify</span>
                                    </button>
                                  </div>
                                  
                                  {/* Photo Number Badge */}
                                  <div className="absolute top-1 left-1 bg-black/70 backdrop-blur-sm px-1.5 py-0.5 rounded text-white text-xs font-bold">
                                    #{photoIdx + 1}
                                  </div>
                                </div>
                              ))}
                            </div>
                            
                            {/* Info Box - Prominent */}
                            <div className="mt-4 bg-gradient-to-br from-cyan-500/20 to-teal-500/20 backdrop-blur border-2 border-cyan-400/40 rounded-xl p-4">
                              <div className="flex items-start gap-3">
                                <div className="w-8 h-8 bg-cyan-500/30 backdrop-blur border border-cyan-400/50 rounded-lg flex items-center justify-center flex-shrink-0">
                                  <span className="text-lg">ℹ️</span>
                                </div>
                                <div>
                                  <p className="text-cyan-200 font-semibold text-sm mb-1">Wie funktioniert's?</p>
                                  <p className="text-cyan-100/90 text-xs leading-relaxed">
                                    Bild antippen, um Details zu ändern – Originalszene bleibt erhalten
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    
                    {/* Divider */}
                    <div className="h-px bg-white/10 my-4"></div>
                    
                    {/* Neues Training - Auswahl zwischen Vehicle & Avatar */}
                    <div className="space-y-3">
                      {/* Button: Vehicle trainieren */}
                      <button
                        onClick={() => {
                          openWizard('vehicle');
                          setAccordionOpen(prev => ({...prev, myVehicles: false}));
                        }}
                        className="w-full p-4 bg-gradient-to-r from-cyan-500/20 to-teal-500/20 border-2 border-cyan-500/50 hover:from-cyan-500/30 hover:to-teal-500/30 transition-all flex items-center gap-3 rounded-xl"
                      >
                        <div className="w-10 h-10 rounded-lg bg-cyan-500 flex items-center justify-center flex-shrink-0">
                          <Car className="w-5 h-5 text-black" />
                        </div>
                        <div className="text-left flex-1">
                          <div className="text-white font-bold text-sm">Neues Vehicle trainieren</div>
                          <div className="text-cyan-300 text-xs">Auto, Motorrad, Bike - 10 Fotos</div>
                        </div>
                      </button>
                      
                      {/* Button: Avatar trainieren */}
                      <button
                        onClick={() => {
                          openWizard('avatar');
                          setAccordionOpen(prev => ({...prev, myVehicles: false}));
                        }}
                        className="w-full p-4 bg-gradient-to-r from-purple-500/20 to-pink-500/20 border-2 border-purple-500/50 hover:from-purple-500/30 hover:to-pink-500/30 transition-all flex items-center gap-3 rounded-xl"
                      >
                        <div className="w-10 h-10 rounded-lg bg-purple-500 flex items-center justify-center flex-shrink-0">
                          <Sparkles className="w-5 h-5 text-black" />
                        </div>
                        <div className="text-left flex-1">
                          <div className="text-white font-bold text-sm">Neuer Avatar trainieren</div>
                          <div className="text-purple-300 text-xs">Person - 10 Fotos für FaceID</div>
                        </div>
                      </button>
                    </div>
                    
                  </div>
                )}
              </div>
            )}
            
          </div>
        )}
        
      </div>

      
      {/* Cyan Verlaufs-Trenner */}
      <div className="mt-16 mb-8 max-w-6xl mx-auto px-4">
        <div className="h-1 bg-gradient-to-r from-transparent via-cyan-400 to-transparent opacity-70 shadow-lg shadow-cyan-400/30"></div>
      </div>
      
      {/* Gallery Header */}
      <div className="max-w-6xl mx-auto px-4 mb-10">
        <h2 className="text-4xl md:text-5xl font-bold text-white text-center mb-3 tracking-tight">
          Deine Galerie
        </h2>
        <p className="text-gray-400 text-center text-base">
          Alle generierten Bilder
        </p>
      </div>

      
      {/* Gallery - Volle Breite auf Mobile */}
      <div className="px-0 md:px-4" data-testid="vehicle-gallery">
        <VehicleGallery
          generations={generations}
          loading={loadingGallery}
          onDelete={handleImageDelete}
          onModify={handleModifyImage}
          onRemix={async (remixData, imageId) => {
            console.log('🎨 Remix requested:', remixData, imageId);
            
            if (!remixData.sourceImage || !currentTraining?.id) {
              alert('Fehler: Kein Bild oder Training ausgewählt');
              return;
            }
            
            setIsGenerating(true);
            
            try {
              // Create FormData for the request
              const formData = new FormData();
              formData.append('training_id', currentTraining.id);
              formData.append('source_image_url', remixData.sourceImage);
              formData.append('remix_prompt', remixData.prompt);
              formData.append('num_images', remixData.num_images || 2);  // WICHTIG: Anzahl mitschicken!
              
              const token = localStorage.getItem('snapshot_token');
              const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
              
              const response = await fetch(`${backendUrl}/api/vehicles/remix`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`
                },
                body: formData
              });
              
              if (!response.ok) {
                const error = await response.json();
                throw new Error(error.detail || 'Remix fehlgeschlagen');
              }
              
              const data = await response.json();
              console.log('✅ Remix erfolgreich:', data);
              
              // Reload gallery to show new images
              await loadGenerations();
              
              // Auto-Scroll zur Galerie nach Remix
              setTimeout(() => {
                const galleryElement = document.querySelector('[data-testid="vehicle-gallery"]');
                if (galleryElement) {
                  galleryElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }, 500);
              
            } catch (error) {
              console.error('❌ Remix failed:', error);
              alert(`Remix fehlgeschlagen: ${error.message}`);
            } finally {
              setIsGenerating(false);
            }
          }}
          trainingId={currentTraining?.id}
          isGenerating={isGenerating}
        />
      </div>
      
      {/* Modify Modal */}
      <VehicleModifyModal
        isOpen={showModifyModal}
        onClose={() => {
          setShowModifyModal(false);
          setModifySourceImage(null);
          setModifyTrainingId(null);
        }}
        sourceImage={modifySourceImage}
        currentTraining={currentTraining}
        onModify={handleModifySubmit}
        isModifying={isModifying}
      />
      
      {/* Templates Modal */}
      {showTemplatesModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-[#0A0E1A] to-[#111827] border border-white/20 rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-br from-white/10 to-transparent backdrop-blur-xl border-b border-white/10 p-6 flex items-center justify-between z-20">
              <div>
                <h3 className="text-2xl font-bold text-white mb-1">Prompt-Vorlagen</h3>
                <p className="text-gray-400 text-sm">{VEHICLE_PROMPT_TEMPLATES.length} professionelle Szenarien</p>
              </div>
              <button
                onClick={() => setShowTemplatesModal(false)}
                className="w-10 h-10 bg-white/10 backdrop-blur-xl border border-white/20 rounded-full flex items-center justify-center hover:bg-white/20 transition-all"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
            
            {/* Handle-Strich */}
            <div className="w-16 h-1 bg-white/20 rounded-full mx-auto mt-6"></div>
            
            {/* Content */}
            <div className="p-6">
              
              {/* Info Banner */}
              <div className="p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-xl mb-6">
                <p className="text-cyan-300 text-sm">
                  🎨 <strong>Klicke auf eine Vorlage</strong>, um sie in dein Prompt-Feld zu übernehmen
                </p>
              </div>
              
              {/* Templates Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(currentTraining?.training_type === 'avatar' ? AVATAR_PROMPT_TEMPLATES : VEHICLE_PROMPT_TEMPLATES).map((template, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      handleUseTemplate(template);
                      setShowTemplatesModal(false);
                    }}
                    className="p-4 bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl hover:bg-white/10 hover:border-cyan-400/50 transition-all text-left group"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-cyan-500/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-cyan-500/30 transition-all">
                        <Sparkles className="w-4 h-4 text-cyan-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium text-sm mb-1 line-clamp-1">{template.short}</p>
                        <p className="text-gray-400 text-xs line-clamp-2">{template.full}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
              
            </div>
            
          </div>
        </div>
      )}
      
      </div>
    </div>
  );
}
